<template>
  <table class="table table-striped table-condensed">
    <thead>
    <tr>
      <th class="col-xs-2">
      </th>

      <th class="col-xs-5">
        <a href="#" @click.prevent="sort('period')">
          {{ granularityLabel }} <i class="fa fa-sort"></i>
        </a>
      </th>

      <th class="col-xs-3">
        <a href="#" @click.prevent="sort('index')">
          Index <i class="fa fa-sort"></i>
        </a>
      </th>

      <th class="col-xs-4">
        <a href="#" @click.prevent="sort('value')">
          Revenue <i class="fa fa-sort"></i>
        </a>
      </th>

      <th class="col-xs-4">
        <a href="#" @click.prevent="sort(targetMetric)">
          Target <i class="fa fa-sort"></i>
        </a>
      </th>

      <th class="col-xs-6">
        <a href="#" @click.prevent="sort('target')">
          Difference <i class="fa fa-sort"></i>
        </a>
      </th>
    </tr>
    </thead>

    <tbody>
    <tr v-for="row in sortedRows">
      <td class="text-center">
        <input type="checkbox" v-model="selected" :value="row">
      </td>

      <td>
        {{ row.period }}
      </td>

      <td>
        <index-number :value="row.index"></index-number>
      </td>

      <td>
        {{ row.value | price }}
      </td>

      <td>
        {{ row[targetMetric] | price }}
      </td>

      <td>
        <difference-number :value="row.difference"></difference-number>
      </td>
    </tr>
    </tbody>

    <tfoot>
    <tr>
      <td></td>

      <td>
        Total
      </td>

      <td>
        <index-number :value="totalIndex"></index-number>
      </td>

      <td>
        {{ totalValue | price }}
      </td>

      <td>
        {{ totalTarget | price }}
      </td>

      <td>
        <difference-number :value="totalDifference"></difference-number>
      </td>
    </tr>

    <tr>
      <td></td>

      <td>
        Selected Total
      </td>

      <td>
        <index-number :value="selectedIndex"></index-number>
      </td>

      <td>
        {{ selectedValue | price }}
      </td>

      <td>
        {{ selectedTarget | price }}
      </td>

      <td>
        <difference-number :value="selectedDifference"></difference-number>
      </td>
    </tr>
    </tfoot>
  </table>
</template>

<script>
import moment from 'moment'
import DifferenceNumber from '@/components/Numbers/DifferenceNumber'
import IndexNumber from '@/components/Numbers/IndexNumber'

export default {
  components: {DifferenceNumber, IndexNumber},

  props: ['year', 'targetMetric', 'selectedChannel', 'granularity', 'rows'],

  data() {
    return {
      sortBy: 'period',
      sortDirection: -1,

      selected: []
    }
  },

  watch: {
    selectedChannel() {
      this.selected = []
    }
  },

  computed: {
    periods() {
      let startDate = moment().year(this.year).startOf('year')

      let endDate = moment().year(this.year).endOf('year')

      let result = []

      while (startDate.isBefore(endDate)) {
        result.push(startDate.clone())

        startDate.add(this.granularityLength, 'month')
      }

      return result
    },

    granularityDimension() {
      if(this.granularity === 'months') {
        return 'publish_month'
      }

      if(this.granularity === 'quarters') {
        return 'publish_quarter'
      }

      return 'publish_year'
    },

    granularityLength() {
      if(this.granularity === 'months') {
        return 1
      }

      if(this.granularity === 'quarters') {
        return 3
      }

      return 12
    },

    granularityFormat() {
      if(this.granularity === 'months') {
        return 'MM/YYYY'
      }

      if(this.granularity === 'quarters') {
        return '[Q]Q/YYYY'
      }

      return 'YYYY'
    },

    granularityLabel() {
      if(this.granularity === 'months') {
        return 'Month'
      }

      if(this.granularity === 'quarters') {
        return 'Quarter'
      }

      return 'Year'
    },

    sortedRows() {
      return this.paddedRows.sort((a, b) => {
        return a[this.sortBy] > b[this.sortBy] ? -this.sortDirection : this.sortDirection
      })
    },

    paddedRows() {
      return this.periods.map(period => {
        let value = this.rows
            .filter(row => !this.selectedChannel || row.channel === this.selectedChannel)
            .filter(row => row[this.granularityDimension] === period.format(this.granularityFormat))
            .reduce((result, row) => result + (row.ad_value_after_costs || 0), 0)

        let target = this.rows
            .filter(row => !this.selectedChannel || row.channel === this.selectedChannel)
            .filter(row => row[this.granularityDimension] === period.format(this.granularityFormat))
            .reduce((result, row) => result + (row[this.targetMetric] || 0), 0)

        return {
          period: period.format(this.granularityFormat),
          value: value,
          [this.targetMetric]: target,
          index: target ? value / target * 100 : null,
          difference: value - target
        }
      })
    },

    totalIndex() {
      if (!this.totalTarget) {
        return null
      }

      return this.totalValue / this.totalTarget * 100
    },

    totalValue() {
      return this.rows
          .filter(row => !this.selectedChannel || row.channel === this.selectedChannel)
          .reduce((total, row) => {
            return total + (row.ad_value_after_costs || 0)
          }, 0)
    },

    totalTarget() {
      return this.rows
          .filter(row => !this.selectedChannel || row.channel === this.selectedChannel)
          .reduce((total, row) => {
            return total + (row[this.targetMetric] || 0)
          }, 0)
    },

    totalDifference() {
      return this.totalValue - this.totalTarget
    },

    selectedIndex() {
      if (!this.selectedTarget) {
        return null
      }

      return this.selectedValue / this.selectedTarget * 100
    },

    selectedValue() {
      return this.selected
          .reduce((total, row) => {
            return total + row.value
          }, 0)
    },

    selectedTarget() {
      return this.selected
          .reduce((total, row) => {
            return total + row[this.targetMetric]
          }, 0)
    },

    selectedDifference() {
      return this.selectedValue - this.selectedTarget
    }
  },

  methods: {
    sort(key) {
      if (this.sortBy === key) {
        return this.sortDirection = -this.sortDirection
      }

      this.sortDirection = key === 'period' ? -1 : 1

      this.sortBy = key
    }
  }
}
</script>
